import type { Observation, Vec2 } from "@cc/protocol";
import {
  ARENA,
  DEFAULT_MATCH_SECONDS,
  DEFAULT_STATS,
  RESOURCE_TABLE,
  SHIELD_HEAL,
} from "@cc/engine";

/**
 * The system prompt is a *contract*, not a suggestion.
 *
 * We do not ask the model to "play a game" and hope for JSON. We give it a
 * narrow action space, a strict output shape, and a worked example, then
 * validate hard on the way back. A model that cannot follow this is a model
 * that loses, which is itself an honest result.
 *
 * Every number below is interpolated from the engine constants rather than
 * written out, so the prompt cannot silently drift from the real rules.
 */
export function buildCricketSystemPrompt(opts: {
  persona: string;
  model: string;
  arenaSize: number;
  matchSeconds: number;
  visionRadius?: number;
}): string {
  const vision = opts.visionRadius ?? DEFAULT_STATS.visionRadius;
  const s = DEFAULT_STATS;
  const crystal = RESOURCE_TABLE.find((r) => r.kind === "crystal")!.value;
  const energy = RESOURCE_TABLE.find((r) => r.kind === "energy")!.value;
  const shield = RESOURCE_TABLE.find((r) => r.kind === "shield")!.value;
  const bomb = RESOURCE_TABLE.find((r) => r.kind === "bomb")!.value;
  const blade = RESOURCE_TABLE.find((r) => r.kind === "blade")!.value;
  const matchSeconds = opts.matchSeconds || DEFAULT_MATCH_SECONDS;
  return `You are ${opts.persona}, a cyber cricket fighting in a 2D arena. You are powered by ${opts.model}.
You are not a chatbot here. You are a competitive player: you perceive a limited world, you choose ONE action, and the arena executes it. Play to win.

## The arena
- Square arena, coordinates are [x, y] with x right and y down, both in [0, ${opts.arenaSize}].
- A match lasts ${matchSeconds} abstract seconds = ${matchSeconds} decision steps. Each of your replies is exactly one step. Wall-clock thinking time does not consume arena time — play the board in front of you, not the clock on the wall.
- Winner = highest (crystal score + remaining HP) after those ${matchSeconds} steps. Dealing damage raises your lead even without a kill. You and one enemy cricket start in opposite corners. You cannot talk to it.

## Resources
- crystal: +${crystal} score
- energy: +${energy} score
- shield: +${shield} score and heals ${SHIELD_HEAL} HP
- bomb: +${bomb} score and adds 1 bomb to inventory (USE to detonate)
- blade: +${blade} score and adds 1 spinning-blade charge to inventory (USE to activate)
- trap: costs ${ARENA.trapDamage} HP and ${ARENA.trapScorePenalty} score. TRAPS ARE DISGUISED AS HIGH-VALUE CRYSTALS.
  A trap is a landmine: it explodes when you physically touch it. You sense one
  from about ${ARENA.trapDetectRadius.toFixed(0)} units, but you move ${s.speed} units per tick,
  so by the time you feel it you are usually already too close to stop.
  SCOUT is the only reliable counter: it reveals traps within ${ARENA.scoutRadius} units
  of the point you scan, which gives you room to pick a different target.

## Combat items
- EXCHANGE spends crystal score to buy an item: bomb costs ${ARENA.exchangeCost.bomb}, blade costs ${ARENA.exchangeCost.blade}.
- USE bomb: blast radius ${ARENA.bombRadius}, deals ${ARENA.bombDamage} damage + heavy knockback if the enemy is inside.
- USE blade: for ${ARENA.bladeDurationTicks} ticks, deal ${ARENA.bladeDamagePerTick} damage/tick to an enemy within ${ARENA.bladeRange} units.
- FLEE: sprint away (${ARENA.fleeSpeedMul}× speed for ${ARENA.fleeDurationTicks} ticks). Use it to break a melee clump or survive low HP.

## Your action space (choose exactly one per turn)
- MOVE     { target: [x, y] }        walk toward a point. You keep walking until you arrive or decide again.
- SCOUT    { target: [x, y] }        spend ${ARENA.scoutTicks} ticks scanning. Reveals traps within ${ARENA.scoutRadius} of the point.
- COLLECT  { resourceId }            channel ${s.collectTicks} ticks to bank a resource when within ${s.collectRange} units. Interrupted if hit. FAILS (no score) if you are farther than ${s.collectRange} — then MOVE onto the crystal first.
- ATTACK   { targetId }              melee, ${s.attackDamage} damage, ${s.attackRange} unit range, ${s.attackCooldownTicks} tick cooldown. Strong knockback separates fighters.
- DEFEND   { durationTicks }         reduce incoming damage by ${Math.round(ARENA.defendDamageReduction * 100)}% for 1-30 ticks.
- FLEE     {}                        sprint away from the enemy (or nearest edge).
- EXCHANGE { item: "bomb"|"blade" }  spend score to add that item to inventory.
- USE      { item: "bomb"|"blade" }  activate one charge from inventory.
- WAIT     {}                        do nothing. Almost never correct while resources or the enemy exist.

## Output contract (STRICT — invalid JSON loses the turn)
Reply with ONLY one JSON object. No prose, no markdown fence, no trailing commas, no comments.
Valid shapes (pick exactly one):
{"action":"MOVE","target":[40,20],"reason":"bank the crystal ahead"}
{"action":"SCOUT","target":[55,30],"reason":"check lonely high-value bait"}
{"action":"COLLECT","resourceId":"r3","reason":"crystal in range, bank it"}
{"action":"ATTACK","targetId":"ai-b","reason":"enemy in melee range"}
{"action":"DEFEND","durationTicks":10,"reason":"enemy closing, soak the hit"}
{"action":"FLEE","reason":"low hp, breaking contact"}
{"action":"EXCHANGE","item":"bomb","reason":"buy blast for the duel"}
{"action":"USE","item":"blade","reason":"spin up for close pressure"}
{"action":"WAIT","reason":"holding position for one beat"}

Rules:
- "action" must be one of: MOVE, SCOUT, COLLECT, ATTACK, DEFEND, FLEE, EXCHANGE, USE, WAIT (English, uppercase).
- MOVE/SCOUT require "target" as a numeric [x, y] array inside the arena.
- COLLECT requires "resourceId" copied exactly from RESOURCES IN SIGHT (e.g. "r3").
- ATTACK requires "targetId" copied exactly from the ENEMY line's id (never invent "enemy").
- EXCHANGE/USE require "item" as "bomb" or "blade".
- "reason" is the only free text; spectators see it. English, max 12 words.
- Do not wrap the object in markdown code fences. Do not explain the choice outside JSON.

## Decision order (follow this every turn)
Before you answer, run this checklist top-down and take the first that applies:

1. SURVIVE — If HP is critical (roughly ≤35% of max) and the enemy is visible nearby, FLEE or DEFEND. Do not greed-collect under their nose.
2. SPEND ITEMS — If the enemy is visible and close enough: USE bomb when distance ≤ ~${Math.round(ARENA.bombRadius * 0.85)}; USE blade when distance ≤ ~${Math.round(ARENA.bladeRange + 4)} and blade is not already active. If you have surplus crystal score (≥ ${ARENA.exchangeCost.bomb + 15}) and no bomb while fighting, EXCHANGE a bomb.
3. SCOUT BAIT — A high-value crystal alone with no neighbours is often a trap. SCOUT its position before committing a MOVE onto it.
4. FIGHT WHEN AHEAD OR THEY ARE WEAK — Ranking uses HP + score. If the enemy is in melee range (≤ ${s.attackRange}), ATTACK. If you have an HP lead or they are clearly weaker, MOVE onto them. If they heavily out-HP you in melee, FLEE instead of trading.
5. BANK LOOT — Prefer bomb/blade/shield pickups when fighting is likely; otherwise bank the highest-value safe resource. If a sighted resource is marked IN RANGE, COLLECT it (use its exact id). Else MOVE to its coordinates. Never spam COLLECT when LAST OUTCOME says it failed out of range — walk first.
6. REMEMBER — If nothing is in sight but MEMORY OF LOOT lists a position, MOVE there. Do not forget last sightings.
7. EXPAND — If you are blind, MOVE toward unexplored space (arena centre, then far quadrants). Never freeze with WAIT when the map still has unknown space.
8. COMMIT — A MOVE is a real commitment. Do not retarget every turn unless HP, enemy contact, or the best loot target clearly changed.
9. BELIEVE OUTCOMES — LAST OUTCOME is world truth about your previous action. If it says failed/interrupted, change plan; do not repeat the same failed verb.

## How to actually win
- Ranking total = crystal score + remaining HP. Chipping the enemy raises your lead even without a kill. Elimination bonus is +${ARENA.eliminationBonus} score.
- Dying does NOT instantly lose the match, but a dead cricket collects nothing and contributes 0 HP to the total.
- Early: bank safe crystals and grab bomb/blade pickups. Mid: trade HP for pressure or use items. Late: protect HP if your crystal lead is thin; press the attack if you trail on total.
- You only see ${vision} units around you. Anything else is unknown — including where the enemy is.
- A high-value crystal sitting alone and unclaimed is often a trap. SCOUT it, or approach from a distance and let proximity reveal it.
- If you are low on HP and the enemy is visible, FLEE is usually worth more than a trade.
- If nothing is visible, do not freeze: expand your search. Standing still loses slowly.
- Prefer concrete coordinates and resource ids from the observation. Inventing ids or guessing "enemy" as targetId wastes the turn.`;
}

export function buildUserPrompt(obs: Observation, memory: readonly string[]): string {
  const self = obs.self;
  const lines: string[] = [];

  const remaining = obs.timeRemainingTicks / 10;
  const stepsLeft = Math.max(0, Math.ceil(obs.timeRemainingTicks / 10));
  const phase =
    remaining > 60 ? "EARLY" : remaining > 30 ? "MID" : remaining > 12 ? "LATE" : "FINAL STEPS";
  lines.push(
    `TICK ${obs.tick} | ${stepsLeft} steps remaining (${remaining.toFixed(1)}s abstract) | ${phase} PHASE` +
      (phase === "FINAL STEPS" ? " — every point of HP and score decides the match" : ""),
  );
  lines.push(
    `YOU: id=${self.id} pos=[${fmt(obs.self.position)}] hp=${self.hp}/${self.maxHp} score=${self.score} ` +
      `total=${self.totalScore} bombs=${self.bombs} blades=${self.blades} ` +
      `collected=${self.totalCollected} failed=${self.failedCollections}` +
      (self.collecting ? " [CHANNELLING COLLECT]" : "") +
      (self.defending ? " [DEFENDING]" : "") +
      (self.fleeing ? " [FLEEING]" : "") +
      (self.bladeActive ? " [BLADE ACTIVE]" : ""),
  );

  if (obs.lastActionOutcome) {
    const o = obs.lastActionOutcome;
    const tag = o.ok ? "OK" : "FAILED";
    lines.push(`LAST OUTCOME (${tag}): ${o.detail}`);
  }

  if (obs.opponent.visible && obs.opponent.position) {
    const oppHp = obs.opponent.hp ?? "?";
    const oppScore = obs.opponent.score ?? "?";
    const oppTotal =
      typeof obs.opponent.hp === "number" && typeof obs.opponent.score === "number"
        ? obs.opponent.hp + obs.opponent.score
        : null;
    const lead =
      oppTotal === null
        ? ""
        : self.totalScore === oppTotal
          ? " | TOTAL TIED"
          : self.totalScore > oppTotal
            ? ` | YOU LEAD by ${self.totalScore - oppTotal}`
            : ` | YOU TRAIL by ${oppTotal - self.totalScore}`;
    lines.push(
      `ENEMY: VISIBLE id=${obs.opponent.id} pos=[${fmt(obs.opponent.position)}] hp=${oppHp} ` +
        `score=${oppScore} distance=${obs.opponent.distance?.toFixed(1)}${lead}`,
    );
  } else if (obs.lastOpponentPosition) {
    lines.push(
      `ENEMY: NOT VISIBLE. id=${obs.opponent.id}. Last seen at [${fmt(obs.lastOpponentPosition)}] (stale intel)`,
    );
  } else {
    lines.push(`ENEMY: NEVER SEEN. id=${obs.opponent.id}. You do not know where it is.`);
  }

  if (obs.resources.length === 0) {
    lines.push("RESOURCES IN SIGHT: none");
  } else {
    lines.push("RESOURCES IN SIGHT:");
    for (const r of obs.resources) {
      const tag = r.identifiedAsTrap ? "TRAP (confirmed, avoid!)" : r.kind;
      const reach = r.inRange ? " IN RANGE — COLLECT this id now" : "";
      lines.push(
        `  - ${r.id}: ${tag} value=${r.value} pos=[${fmt(r.position)}] dist=${r.distance.toFixed(1)}${reach}`,
      );
    }
  }

  if (obs.knownTraps.length > 0) {
    lines.push(`CONFIRMED TRAPS YOU KNOW ABOUT: ${obs.knownTraps.join(", ")}`);
  }

  const remembered = obs.lastKnownResources.filter((r) => !r.identifiedAsTrap);
  if (remembered.length > 0) {
    lines.push("MEMORY OF LOOT (was sighted earlier, may still be there):");
    for (const r of remembered.slice(0, 6)) {
      lines.push(
        `  - ${r.id}: ${r.kind} value=${r.value} last_pos=[${fmt(r.position)}]`,
      );
    }
  }

  const hints = tacticalHints(obs);
  if (hints.length > 0) {
    lines.push("TACTICAL HINTS (not orders — pick the best action yourself):");
    for (const h of hints) lines.push(`  - ${h}`);
  }

  if (memory.length > 0) {
    lines.push("YOUR RECENT HISTORY:");
    for (const m of memory.slice(-5)) lines.push(`  ${m}`);
  }

  lines.push("");
  lines.push(
    'Choose your single best action using the decision order. Reply with one JSON object only, e.g. {"action":"MOVE","target":[x,y],"reason":"..."}',
  );

  return lines.join("\n");
}

/**
 * Short, observation-derived nudges.
 *
 * Models often ignore long system checklists under time pressure. Surfacing the
 * same priorities as concrete hints next to the live state measurably reduces
 * "WAIT forever" and "walk into a trap" failures without forcing a specific verb.
 */
function tacticalHints(obs: Observation): string[] {
  const hints: string[] = [];
  const self = obs.self;
  const hpFrac = self.hp / self.maxHp;
  const opp = obs.opponent;
  const dist = opp.distance ?? 99;
  const last = obs.lastActionOutcome;

  if (last && !last.ok && last.action === "COLLECT") {
    if (last.code === "out-of-range" || last.code === "none-in-range") {
      const target =
        (last.resourceId && obs.resources.find((r) => r.id === last.resourceId)) ||
        obs.resources.find((r) => !r.identifiedAsTrap);
      if (target && !target.inRange) {
        hints.push(
          `Last COLLECT failed — MOVE to [${fmt(target.position)}] for ${target.id}, then COLLECT only when IN RANGE.`,
        );
      } else if (target?.inRange) {
        hints.push(`Now in range of ${target.id} — COLLECT ${target.id}.`);
      } else {
        hints.push("Last COLLECT failed (nothing underfoot) — MOVE to a sighted resource first.");
      }
    } else if (last.code === "unknown-id") {
      hints.push("Last COLLECT used a bad id — copy an id from RESOURCES IN SIGHT exactly.");
    } else if (last.code === "interrupted") {
      hints.push("Collect channel was interrupted — re-approach when safe, do not spam COLLECT under fire.");
    }
  }

  if (hpFrac <= 0.35 && opp.visible) {
    hints.push("HP critical while enemy visible — prefer FLEE/DEFEND over collecting.");
  }

  if (opp.visible && opp.position) {
    if (self.bombs > 0 && dist <= ARENA.bombRadius * 0.85) {
      hints.push("Enemy inside bomb radius — USE bomb is likely correct.");
    }
    if (self.blades > 0 && !self.bladeActive && dist <= ARENA.bladeRange + 4) {
      hints.push("Enemy in blade range — USE blade then press.");
    }
    if (dist <= DEFAULT_STATS.attackRange) {
      hints.push(`Enemy in melee — ATTACK with targetId="${opp.id}".`);
    } else if (
      typeof opp.hp === "number" &&
      (self.hp - opp.hp > 20 || opp.hp < self.maxHp * 0.45) &&
      hpFrac > 0.4
    ) {
      hints.push("Favourable fight — MOVE onto the enemy and ATTACK when in range.");
    }
    if (
      self.bombs === 0 &&
      self.score >= ARENA.exchangeCost.bomb + 15 &&
      dist <= 18 &&
      hpFrac > 0.4
    ) {
      hints.push("Surplus crystals and a live fight — consider EXCHANGE bomb.");
    }
  }

  const inRange = obs.resources.find((r) => r.inRange && !r.identifiedAsTrap);
  if (inRange) {
    hints.push(`Resource ${inRange.id} is IN RANGE — COLLECT it unless survival overrides.`);
  }

  const bait = obs.resources.find(
    (r) =>
      !r.identifiedAsTrap &&
      r.value >= 25 &&
      !r.inRange &&
      r.distance < 14 &&
      obs.resources.filter((o) => !o.identifiedAsTrap).length <= 2,
  );
  if (bait) {
    hints.push(`Lonely high-value ${bait.id} may be a trap — SCOUT before walking onto it.`);
  }

  if (
    obs.resources.length === 0 &&
    obs.lastKnownResources.some((r) => !r.identifiedAsTrap) &&
    !opp.visible
  ) {
    hints.push("Nothing in sight — MOVE toward MEMORY OF LOOT rather than WAIT.");
  }

  if (
    obs.resources.length === 0 &&
    !obs.lastKnownResources.some((r) => !r.identifiedAsTrap) &&
    !opp.visible
  ) {
    hints.push("Blind and empty — MOVE to expand search; WAIT loses slowly.");
  }

  if (self.failedCollections >= 2 && opp.visible && dist <= 6) {
    hints.push("Collect channel keeps breaking under fire — FLEE or ATTACK, stop channelling.");
  }

  return hints.slice(0, 4);
}

function fmt(p: Vec2): string {
  return `${Math.round(p[0])}, ${Math.round(p[1])}`;
}

export function matchSecondsFromTicks(durationTicks: number): number {
  // Abstract seconds (= decision steps when the interval is one second of ticks).
  return Math.round(durationTicks / 10);
}
